import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { InvoiceStatusBadge } from '@/components/admin/InvoiceStatusBadge'
import { InvoiceReminderModal } from '@/components/admin/InvoiceReminderModal'
import { InvoicePaymentThankYouModal } from '@/components/admin/InvoicePaymentThankYouModal'
import { useInvoiceById, useScheduledReminders, useUpdateInvoiceStatus } from '@/hooks/useInvoices'
import { formatCurrency, formatDate, cn } from '@/lib/utils'
import { downloadInvoicePDF, previewInvoicePDF } from '@/services/pdf'
import type { InvoiceLine, InvoiceStatus } from '@/types/invoice'
import {
  ChevronLeft,
  Download,
  Send,
  Bell,
  Heart,
  CheckCircle,
  XCircle,
  FileText,
  User,
  Calendar,
  Building,
  Mail,
  ExternalLink,
  History,
} from 'lucide-react'
import toast from 'react-hot-toast'

// Email validation helper
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Validate blob URL (only allow our generated blob URLs)
function isValidBlobUrl(url: string): boolean {
  return url.startsWith('blob:') && url.includes(window.location.origin)
}

// Timeline event component
interface TimelineEventProps {
  icon: React.ReactNode
  title: string
  date: string
  description?: string
  isLast?: boolean
}

function TimelineEvent({ icon, title, date, description, isLast }: TimelineEventProps) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-full bg-beige-100 flex items-center justify-center text-brown-600">
          {icon}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-beige-200 my-1" />}
      </div>
      <div className={cn('pb-4', isLast && 'pb-0')}>
        <p className="font-medium text-brown-900">{title}</p>
        <p className="text-sm text-brown-500">{date}</p>
        {description && <p className="text-sm text-brown-600 mt-1">{description}</p>}
      </div>
    </div>
  )
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: invoice, isLoading, error } = useInvoiceById(id || '')
  const updateStatus = useUpdateInvoiceStatus()
  const { data: scheduledReminders } = useScheduledReminders(invoice?.id, 'reminder')
  const hasPendingReminder = (scheduledReminders ?? []).some((r) => r.status === 'pending')
  const { data: scheduledThankYous } = useScheduledReminders(invoice?.id, 'thank_you')
  const hasPendingThankYou = (scheduledThankYous ?? []).some((r) => r.status === 'pending')

  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showThankYouModal, setShowThankYouModal] = useState(false)
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0])

  // Clean up PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl)
      }
    }
  }, [pdfPreviewUrl])

  // Determine if overdue
  const isOverdue =
    invoice &&
    (invoice.statut === 'en_attente' || invoice.statut === 'sent') &&
    new Date(invoice.date_echeance) < new Date()

  const displayStatus: InvoiceStatus = isOverdue ? 'overdue' : (invoice?.statut as InvoiceStatus)

  // Action handlers
  const handleDownload = async () => {
    if (!invoice) return
    try {
      await downloadInvoicePDF(invoice)
      toast.success('PDF telecharge')
    } catch {
      toast.error('Erreur lors du telechargement')
    }
  }

  const handlePreview = async () => {
    if (!invoice) return
    try {
      const url = await previewInvoicePDF(invoice)
      setPdfPreviewUrl(url)
    } catch {
      toast.error('Erreur lors de la generation du PDF')
    }
  }

  const handleSendInvoice = async () => {
    if (!invoice) return
    try {
      await updateStatus.mutateAsync({ id: invoice.id, statut: 'en_attente' })
      toast.success('Facture envoyee')
    } catch {
      toast.error('Erreur lors de l\'envoi')
    }
  }

  const handleMarkAsPaid = async () => {
    if (!invoice) return
    try {
      await updateStatus.mutateAsync({
        id: invoice.id,
        statut: 'payee',
        date_paiement: paymentDate,
      })
      setShowPaymentModal(false)
      toast.success('Facture marquee comme payee')
    } catch {
      toast.error('Erreur lors de la mise a jour')
    }
  }

  const handleCancel = async () => {
    if (!invoice) return
    try {
      await updateStatus.mutateAsync({ id: invoice.id, statut: 'cancelled' })
      setShowCancelModal(false)
      toast.success('Facture annulee')
    } catch {
      toast.error('Erreur lors de l\'annulation')
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    )
  }

  // Error state
  if (error || !invoice) {
    return (
      <div className="text-center py-24">
        <FileText className="w-16 h-16 mx-auto mb-4 text-beige-300" />
        <h2 className="text-xl font-semibold text-brown-900 mb-2">
          Facture non trouvee
        </h2>
        <p className="text-brown-600 mb-4">
          La facture demandee n'existe pas ou a ete supprimee.
        </p>
        <Link to="/invoices">
          <Button variant="outline">Retour aux factures</Button>
        </Link>
      </div>
    )
  }

  const lines = (invoice.produits_json || []) as InvoiceLine[]

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/invoices"
          className="text-brown-500 hover:text-brown-700 flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          Factures
        </Link>
        <span className="text-brown-400">/</span>
        <span className="text-brown-700">{invoice.numero}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gold-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-gold-600" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-brown-900">
              {invoice.numero}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <InvoiceStatusBadge statut={displayStatus} size="md" />
              <span className="text-brown-600">
                {formatCurrency(invoice.total_ttc || 0)}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handlePreview}>
            <ExternalLink className="w-4 h-4 mr-1" />
            Apercu
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
          {invoice.statut === 'draft' && (
            <Button size="sm" onClick={handleSendInvoice} isLoading={updateStatus.isPending}>
              <Send className="w-4 h-4 mr-1" />
              Envoyer
            </Button>
          )}
          {(invoice.statut === 'en_attente' || invoice.statut === 'sent' || isOverdue) && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReminderModal(true)}
                className="relative"
              >
                <Bell className="w-4 h-4 mr-1" />
                Relancer
                {hasPendingReminder && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                )}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowPaymentModal(true)}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Marquer payee
              </Button>
            </>
          )}
          {invoice.statut === 'payee' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowThankYouModal(true)}
              className="relative"
            >
              <Heart className="w-4 h-4 mr-1" />
              Remercier
              {hasPendingThankYou && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-mint-500 ring-2 ring-white" />
              )}
            </Button>
          )}
          {invoice.statut !== 'cancelled' && invoice.statut !== 'payee' && (
            <Button variant="danger" size="sm" onClick={() => setShowCancelModal(true)}>
              <XCircle className="w-4 h-4 mr-1" />
              Annuler
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice details */}
          <Card>
            <CardHeader>
              <CardTitle>Details de la facture</CardTitle>
            </CardHeader>
            <div className="p-6 pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-brown-500">Date de creation</span>
                  <p className="font-medium">{formatDate(invoice.date_creation)}</p>
                </div>
                <div>
                  <span className="text-brown-500">Date d'echeance</span>
                  <p className={cn('font-medium', isOverdue && 'text-red-600')}>
                    {formatDate(invoice.date_echeance)}
                  </p>
                </div>
                {invoice.date_paiement && (
                  <div>
                    <span className="text-brown-500">Date de paiement</span>
                    <p className="font-medium text-mint-600">
                      {formatDate(invoice.date_paiement)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Line items */}
          <Card>
            <CardHeader>
              <CardTitle>Prestations</CardTitle>
            </CardHeader>
            <div className="p-6 pt-0">
              <div className="border border-beige-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-beige-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-brown-600">
                        Description
                      </th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-brown-600">
                        Qte
                      </th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-brown-600">
                        Prix HT
                      </th>
                      <th className="px-4 py-2 text-center text-sm font-medium text-brown-600">
                        TVA
                      </th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-brown-600">
                        Total HT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line, index) => (
                      <tr key={index} className="border-t border-beige-200">
                        <td className="px-4 py-3 text-sm text-brown-900">
                          {line.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-brown-600">
                          {line.quantite}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-brown-600">
                          {formatCurrency(line.prix_unitaire)}
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-brown-600">
                          {line.taux_tva}%
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-brown-900">
                          {formatCurrency(line.total_ht)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end mt-4">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-brown-600">Sous-total HT</span>
                    <span className="font-medium">
                      {formatCurrency(invoice.total_ht || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-brown-500">TVA</span>
                    <span>{formatCurrency(invoice.tva || 0)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-beige-200 text-lg font-bold">
                    <span>Total TTC</span>
                    <span className="text-mint-600">
                      {formatCurrency(invoice.total_ttc || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Conditions */}
          {invoice.conditions && (
            <Card>
              <CardHeader>
                <CardTitle>Conditions de paiement</CardTitle>
              </CardHeader>
              <div className="p-6 pt-0">
                <p className="text-sm text-brown-600">{invoice.conditions}</p>
              </div>
            </Card>
          )}

          {/* Internal notes */}
          {invoice.notes_internes && (
            <Card className="border-l-4 border-l-gold-400">
              <CardHeader>
                <CardTitle>Notes internes</CardTitle>
              </CardHeader>
              <div className="p-6 pt-0">
                <p className="text-sm text-brown-600">{invoice.notes_internes}</p>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Client
              </CardTitle>
            </CardHeader>
            <div className="p-6 pt-0 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-mint-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-mint-600" />
                </div>
                <div>
                  <p className="font-medium text-brown-900">
                    {invoice.client?.full_name || 'Client inconnu'}
                  </p>
                  {invoice.client?.company && (
                    <p className="text-sm text-brown-500 flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {invoice.client.company}
                    </p>
                  )}
                </div>
              </div>
              {invoice.client?.email && isValidEmail(invoice.client.email) && (
                <div className="flex items-center gap-2 text-sm text-brown-600">
                  <Mail className="w-4 h-4" />
                  <a
                    href={`mailto:${encodeURIComponent(invoice.client.email)}`}
                    className="hover:text-mint-600"
                  >
                    {invoice.client.email}
                  </a>
                </div>
              )}
            </div>
          </Card>

          {/* Project link */}
          {invoice.projet && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Projet associe
                </CardTitle>
              </CardHeader>
              <div className="p-6 pt-0">
                <Link
                  to={`/projects/${invoice.projet.id}`}
                  className="text-mint-600 hover:underline"
                >
                  {invoice.projet.title}
                </Link>
              </div>
            </Card>
          )}

          {/* Timeline / History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Historique
              </CardTitle>
            </CardHeader>
            <div className="p-6 pt-0">
              <div className="space-y-0">
                <TimelineEvent
                  icon={<FileText className="w-4 h-4" />}
                  title="Facture creee"
                  date={formatDate(invoice.created_at)}
                />
                {invoice.statut !== 'draft' && (
                  <TimelineEvent
                    icon={<Send className="w-4 h-4" />}
                    title="Facture envoyee"
                    date={formatDate(invoice.date_creation)}
                  />
                )}
                {invoice.date_paiement && (
                  <TimelineEvent
                    icon={<CheckCircle className="w-4 h-4" />}
                    title="Paiement recu"
                    date={formatDate(invoice.date_paiement)}
                    isLast
                  />
                )}
                {invoice.statut === 'cancelled' && (
                  <TimelineEvent
                    icon={<XCircle className="w-4 h-4" />}
                    title="Facture annulee"
                    date={formatDate(invoice.updated_at)}
                    isLast
                  />
                )}
                {!invoice.date_paiement && invoice.statut !== 'cancelled' && (
                  <TimelineEvent
                    icon={<Calendar className="w-4 h-4" />}
                    title="Echeance"
                    date={formatDate(invoice.date_echeance)}
                    description={isOverdue ? 'En retard' : undefined}
                    isLast
                  />
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {pdfPreviewUrl && isValidBlobUrl(pdfPreviewUrl) && (
        <Modal
          isOpen={!!pdfPreviewUrl}
          onClose={() => {
            URL.revokeObjectURL(pdfPreviewUrl)
            setPdfPreviewUrl(null)
          }}
          title={`Apercu - ${invoice.numero}`}
          size="xl"
        >
          <div className="h-[600px]">
            <iframe
              src={pdfPreviewUrl}
              className="w-full h-full border rounded-lg"
              title="PDF Preview"
              sandbox="allow-same-origin"
            />
          </div>
        </Modal>
      )}

      {/* Payment confirmation modal */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Marquer comme payee"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Confirmez-vous que cette facture a ete payee ?
          </p>
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-1">
              Date de paiement
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleMarkAsPaid} isLoading={updateStatus.isPending}>
              Confirmer le paiement
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Annuler la facture"
      >
        <div className="space-y-4">
          <p className="text-brown-600">
            Etes-vous sur de vouloir annuler cette facture ? Cette action est irreversible.
          </p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Non, garder
            </Button>
            <Button variant="danger" onClick={handleCancel} isLoading={updateStatus.isPending}>
              Oui, annuler la facture
            </Button>
          </div>
        </div>
      </Modal>

      <InvoiceReminderModal
        invoice={invoice}
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
      />

      <InvoicePaymentThankYouModal
        invoice={invoice}
        isOpen={showThankYouModal}
        onClose={() => setShowThankYouModal(false)}
      />
    </div>
  )
}
